import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";

const NAV_LINKS = [
  { href: "#services", label: "Services" },
  { href: "#about", label: "About" },
  { href: "#gallery", label: "Gallery" },
  { href: "#testimonials", label: "Reviews" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar({ content }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border,backdrop-filter] duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-neutral-200"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#top" className="flex items-center gap-3" data-testid="navbar-logo">
          {content.logo_url ? (
            <img
              src={content.logo_url}
              alt={content.business_name}
              className="h-12 w-auto object-contain"
            />
          ) : (
            <span className="font-display text-2xl font-black uppercase">{content.business_name}</span>
          )}
          <span className="hidden sm:block font-display text-xl font-bold uppercase tracking-tight leading-none">
            {content.business_name}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="font-body text-sm uppercase tracking-widest text-neutral-700 hover:text-orange-600 transition-colors"
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${content.phone.replace(/[^+\d]/g, "")}`}
            className="hidden md:inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-display font-bold uppercase tracking-wider text-sm px-5 py-3 transition-colors"
            data-testid="navbar-call-btn"
          >
            <Phone className="w-4 h-4" />
            {content.phone}
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-11 h-11 flex items-center justify-center bg-neutral-900 text-white"
            aria-label="Toggle menu"
            data-testid="navbar-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-neutral-200" data-testid="mobile-menu">
          <nav className="flex flex-col p-6 gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-display uppercase text-lg tracking-tight"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${content.phone.replace(/[^+\d]/g, "")}`}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-orange-600 text-white font-display font-bold uppercase text-sm px-5 py-4"
              data-testid="mobile-call-btn"
            >
              <Phone className="w-4 h-4" />
              {content.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
